import { useEffect } from 'react'
import { useWebsiteStore } from '../store/useWebsiteStore'

/** How long the editor waits after the last keystroke before saving. */
const DEBOUNCE_MS = 3000

/**
 * Saves quietly, a few seconds after the last change.
 *
 * The draft itself is already durable the moment it changes, because the store
 * persists. The debounce exists for the request that will replace the comment
 * below once `WEBSITE_USE_API` is on: a PUT per keystroke would be one per
 * keystroke.
 *
 * Nothing on screen reports the state. This app tells people about saves the
 * way `useMobileAppManagementQuery` does, with a snackbar when something
 * actually happened and a failure that needs acting on; a permanent "Saved"
 * label in a toolbar is text nobody reads. So: success is silent, and when the
 * request exists, failure goes to `enqueueSnackbar`.
 */
export function useAutosave() {
  const saveState = useWebsiteStore(s => s.saveState)
  const setSaveState = useWebsiteStore(s => s.setSaveState)

  useEffect(() => {
    if (saveState !== 'dirty') return undefined

    const timeout = window.setTimeout(() => {
      setSaveState('saving')
      // The PUT goes here. It is followed by setSaveState('saved') on success
      // and by a snackbar on failure, which is why the state has three values
      // and not a boolean.
      setSaveState('saved')
    }, DEBOUNCE_MS)

    return () => window.clearTimeout(timeout)
  }, [saveState, setSaveState])
}
